// English-only UI strings for the web app (product is English-only — a hard
// boundary, enforced in the backend by i18n.assert_no_portuguese and the CI
// guard). Keep every user-facing label here so the surface stays reviewable.

export const T = {
  app: {
    title: "VisionHammer Graphy",
    overview: "Overview",
    map: "System map",
    explore: "Explore",
    tools: "Tools",
    contributors: "Contributors",
    wiki: "Wiki",
    reviews: "Reviews",
    project: "Project",
  },
  reviews: {
    title: "Pull requests",
    none: "No pull requests yet. Review insights appear once this repository's pull requests are connected.",
    queue: "Queue",
    back: "Back to pull requests",
    ci: "CI",
    review: "Review",
    diff: "Changed symbols",
    base: "Base",
    head: "Head",
    impact: "Impact",
    downstream: "Downstream consumers",
    direct: "Direct consumers",
    overlap: "Overlapping pull requests",
    findings: "Linked findings",
    none2: "None.",
    stalePinned: "This impact was pinned to an earlier head commit and is stale. Re-open to recompute.",
    pinnedTo: (head: string) => `Analysis pinned to head ${head}`,
    viewInExplorer: "View in explorer",
    openSource: "View source",
    reviewSection: "Review",
    localStatus: "Your review status",
    status: { none: "None", commented: "Commented", approved_local: "Approved (local)", changes_requested_local: "Changes requested (local)" },
    notes: "Comments",
    addComment: "Add comment",
    comment: "Comment",
    post: "Post",
    requestReviewer: "Request reviewer",
    requestedReviewers: "Requested reviewers",
    externalPosts: "External delivery",
    localOnlyHint: "Comments and status are local to Graphy. External delivery requires a separate granted capability. Graphy never merges a pull request.",
    activity: "Activity",
  },
  wiki: {
    title: "Architecture wiki",
    index: "Articles",
    none: "No articles yet for this project.",
    reviewState: { generated: "Generated", reviewed: "Reviewed", edited: "Edited" },
    reviewedBy: (who: string) => `Reviewed by ${who}`,
    stale: "Source has changed since this article was written.",
    staleBadge: "Stale",
    staleHint: "A regenerated version is available as a preview. Your reviewed content is unchanged until you accept it.",
    review: "Mark reviewed",
    preview: "Regenerated preview",
    accept: "Accept regeneration",
    discard: "Keep current",
    sources: "Sources",
    exploreArea: "Explore this area",
    askArea: "Ask about this area",
    askUnavailable: "Ask is not available for this project yet.",
    back: "Back to articles",
    snapshot: (id: string) => `Snapshot ${id}`,
  },
  contributors: {
    title: "Contributors",
    community: "Community",
    members: (n: number) => `${n} members`,
    owners: "Owners",
    concentrated: "Concentrated knowledge",
    concentratedHint: "Most of this area is owned by one party — a knowledge-concentration risk, not a vulnerability.",
    unknown: (pct: number) => `${pct}% unknown ownership`,
    escalation: "Escalation (advisory)",
    corrected: "Corrected",
    viewInExplorer: "View in explorer",
    correct: "Correct ownership",
    ownersInput: "Owners (comma-separated)",
    reason: "Reason",
    apply: "Apply correction",
    correctionFailed: "You do not have permission to correct ownership.",
    none: "No contributor data yet. Ownership insights need this repository's commit history, which isn't connected for this project.",
  },
  mcp: {
    title: "MCP tools",
    pick: "Tool",
    execute: "Execute",
    cancel: "Cancel",
    copy: "Copy result",
    copied: "Copied",
    exportJson: "Export JSON",
    required: "required",
    result: "Result",
    provenance: "Provenance",
    noTools: "No tools are available for this project.",
    cancelled: "The request was cancelled.",
    truncated: "Result truncated by the query budget.",
    missingCapability: "This capability is not available.",
    openSource: "View source",
  },
  notifications: {
    title: "Notifications",
    open: "Notifications",
    empty: "No recent updates.",
    markRead: "Mark all read",
    kind: { new: "New", updated: "Updated", withdrawn: "Withdrawn" },
  },
  overviewView: {
    newFindings: (n: number) => `${n} new update${n === 1 ? "" : "s"} since you opened`,
    refresh: "Refresh",
    findings: "Findings",
    reviews: "Reviews",
    verification: "Verification",
    knowledge: "Knowledge",
    communities: "Communities",
    coverage: "Snapshot coverage",
    recent: "Recent findings",
    bySeverity: "By severity",
    byStatus: "By status",
    entryPoints: "Investigate",
    noFindings: "No confirmed findings yet.",
    communitiesCount: (n: number, largest: number) => `${n} communities · largest ${largest}`,
    coverageDetail: (repos: string, symbols: number) => `${repos} · ${symbols} symbols`,
  },
  common: {
    loading: "Loading…",
    error: "This couldn’t be loaded. Check your connection and try again.",
    retry: "Retry",
    reset: "Reset view",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    graphView: "Graph view",
    listView: "List view",
    clear: "Clear",
    noMatch: "No results match your filters.",
    partialCoverage: "Showing a partial view — the result hit the query budget.",
    noSnapshot: "No repository has been indexed yet. Confirmed findings are still delivered and shown.",
    staleReference: "This link points to content that has changed. Showing the latest authorized view.",
    staleSymbol: "The symbol in this link is no longer in the current snapshot. Showing a default view.",
    dismiss: "Dismiss",
    connecting: "Connecting to your VisionHammer session…",
    notConnected: "Could not connect to your VisionHammer session. Open Graphy from the VisionHammer platform.",
  },
  map: {
    search: "Search the map…",
    severity: "Severity",
    status: "Status",
    all: "All",
    legend: "Legend",
    inspector: "Inspector",
    selectHint: "Select a node to inspect it.",
    drillDown: "Open in explorer",
    affectedOn: "Highlight affected set",
    affectedClear: "Clear affected set",
    coverage: "Snapshot coverage",
  },
  explorer: {
    modes: {
      neighborhood: "Symbol neighborhood",
      communities: "Communities",
      path: "Path between symbols",
      hubs: "Hubs",
    },
    origin: "Source symbol",
    destination: "Destination symbol",
    community: "Community",
    provenance: "Provenance",
    search: "Search this subgraph…",
    depth: "Hops",
    direction: "Traversal direction",
    directionBoth: "Both directions",
    directionIncoming: "Who depends on this node",
    directionOutgoing: "What this node uses",
    ignoreDirection: "Ignore direction",
    noPath: "No path with these filters and direction. Absence of a path does not prove independence.",
    pathHops: (n: number) => `${n} hops`,
    hubsTitle: "Most connected symbols in this snapshot",
    count: (shown: number, total: number) => `${shown} of ${total} nodes`,
    truncated: "Result truncated by the query budget — narrow the scope or page further.",
    missingOrigin: "The selected symbol is not in this snapshot.",
  },
  provenanceLabel: {
    extracted: "Extracted",
    inferred: "Inferred",
    ambiguous: "Ambiguous",
  },
  source: {
    title: "Source",
    viewSource: "View source",
    close: "Close",
    upstream: "Callers (upstream)",
    downstream: "Dependencies (downstream)",
    openInExplorer: "Open in explorer",
    truncated: "Showing an excerpt — surrounding lines are not shown.",
    none: "None in this snapshot.",
    unavailable: "Source is not available for this symbol.",
  },
} as const;
